import { uniqueId } from '@comeon/utility';
import { SelectionDto } from '../../../api/sbapi/sbapi-api-model';
import { BetType } from '../../../sportsbook-types';
import {
	BetPlacementStatus,
	BetslipBet,
	BetslipBetError,
	BetslipBetErrorType,
	BetslipDataItem,
	AcceptMode,
	BetslipBetItem,
	BetslipPersistedBet,
	BetslipTab,
	BetslipBetWarning,
	BetslipBetWarningType
} from '../sportsbook-betslip-types';
import {
	BetBonus,
	BetBonusType,
	CheckBetsResponseError,
	CheckBetsResponseWarning,
	PlaceBetsResponseBetStatus,
	PlaceBetsResponseError
} from '../../../api/betting-service/betting-service-types';
import multiply from '../../../utils/muiltiply';

// Range is a slow implementation if we aim to support a high start value, so let's keep it private for now
const range = (start: number, stop: number) => [...Array(stop + 1).keys()].slice(start);

// Math helper to calculate betCount
const factorial = (n: number): number => (n < 2 ? 1 : n * factorial(n - 1));
const combinations = (n: number, r: number): number => factorial(n) / (factorial(r) * factorial(n - r));
const calculateBetCount = (itemCount: number, combination: number): number => combinations(itemCount, combination);

const calculateBetOdds = (dataItems: Array<BetslipDataItem>) =>
	dataItems.reduce((result, item) => multiply(result, item.odds, 2), 1);

export const calculateBetFinalOdds = (betItems: Array<BetslipBetItem>, betBonus?: BetBonus): number => {
	const finalOdds = betItems.reduce((result, item) => multiply(result, item.finalOdds, 2), 1);
	const oddsBonusAmount =
		betBonus?.bonusType === BetBonusType.BET_BOOST ? multiply(finalOdds, betBonus.boostPercentage / 100, 2) : 0;

	return finalOdds + oddsBonusAmount;
};

/**
 * Returns a list of betItems for all passed dataItems, uses existing betItems if they already exist.
 */
const createOrUpdateBetItems = (
	betItems: Array<BetslipBetItem>,
	dataItems: Array<BetslipDataItem>
): Array<BetslipBetItem> => {
	return dataItems.map(dataItem => ({
		...(betItems.find(betItem => betItem.ref === dataItem.ref) || {
			ref: dataItem.ref,
			selectionId: dataItem.selectionId,
			tag: dataItem.tag
		}),
		finalOdds: dataItem.odds
	}));
};

/**
 * Returns a list of all possible single bets based on items
 */
export const createOrUpdateSingleBets = (
	bets: Array<BetslipBet>,
	dataItems: Array<BetslipDataItem>
): Array<BetslipBet> =>
	dataItems.map(dataItem => {
		// Find the existing bet
		let singleBet =
			bets.find(bet => bet.type === BetType.SINGLE && bet.items.every(betItem => betItem.ref === dataItem.ref)) ||
			// Or create a new bet
			(() => {
				const items = [
					{
						ref: dataItem.ref,
						selectionId: dataItem.selectionId,
						finalOdds: dataItem.odds,
						tag: dataItem.tag
					}
				];

				return {
					ref: uniqueId(Date.now().toString()),
					originalBetRef: uniqueId(Date.now().toString()),
					isChecked: false,
					placementStatus: BetPlacementStatus.EDIT,
					type: BetType.SINGLE,
					stake: 0,
					combination: 1,
					betCount: 1,
					// Initially calculate final odds to display probable result ASAP.
					finalOdds: calculateBetFinalOdds(items),
					bonuses: [],
					items,
					errors: [],
					warnings: [],
					acceptAllOddChanges: false
				} as BetslipBet;
			})();

		// Update items on bet.
		singleBet = {
			...singleBet,
			items: createOrUpdateBetItems(singleBet.items, [dataItem])
		};

		return singleBet;
	});

/**
 * Returns a combobet for the items.
 */
export const createOrUpdateComboBet = (bets: Array<BetslipBet>, dataItems: Array<BetslipDataItem>): BetslipBet => {
	let comboBet =
		// Find existing bet
		bets.find(bet => bet.type === BetType.COMBO) ||
		// Or create a new bet
		({
			ref: uniqueId(Date.now().toString()),
			originalBetRef: uniqueId(Date.now().toString()),
			isChecked: false,
			placementStatus: BetPlacementStatus.EDIT,
			type: BetType.COMBO,
			stake: 0,
			combination: dataItems.length,
			betCount: 1,
			bonuses: [],
			items: [],
			errors: [],
			warnings: [],
			acceptAllOddChanges: false
		} as BetslipBet);

	// Refresh combo item array and combination. Update odds and final odds
	const items = createOrUpdateBetItems(comboBet.items, dataItems);
	// Need to calculate odds only for changes in items array to show probable odds to user ASAP.
	const shouldCalculateOdds = comboBet.items.length !== items.length;

	comboBet = {
		...comboBet,
		combination: dataItems.length,
		// To show real time changes when removing an item and having finalOdds different to odds.
		// For example, when bet boost is selected and we use bet.odds as initial odds.
		odds: shouldCalculateOdds ? calculateBetOdds(dataItems) : comboBet.odds,
		finalOdds: shouldCalculateOdds ? calculateBetFinalOdds(items, comboBet.selectedBonus) : comboBet.finalOdds,
		items
	};

	return comboBet;
};

/**
 * Returns a list of all possible system bets based on items
 */
export const createOrUpdateSystemBets = (
	bets: Array<BetslipBet>,
	dataItems: Array<BetslipDataItem>
): Array<BetslipBet> => {
	const existingSystemBets = bets.filter(bet => bet.type === BetType.SYSTEM);

	return range(1, dataItems.length).map(combination => {
		let systemBet =
			// Find existing combination system
			existingSystemBets.find(bet => bet.combination === combination) ||
			// Or create a new one
			({
				ref: uniqueId(Date.now().toString()),
				originalBetRef: uniqueId(Date.now().toString()),
				isChecked: false,
				placementStatus: BetPlacementStatus.EDIT,
				type: BetType.SYSTEM,
				stake: 0,
				combination,
				betCount: 0,
				bonuses: [],
				items: [],
				errors: [],
				warnings: [],
				acceptAllOddChanges: false
			} as BetslipBet);

		systemBet = {
			...systemBet,
			betCount: calculateBetCount(dataItems.length, combination),
			items: createOrUpdateBetItems(systemBet.items, dataItems)
		};

		return systemBet;
	});
};

/**
 * Return a list of replaced bets from persisted bets.
 */
export const replacePersistedBets = (bets: Array<BetslipPersistedBet>) =>
	bets.map(
		bet =>
			({
				ref: uniqueId(Date.now().toString()),
				originalBetRef: uniqueId(Date.now().toString()),
				isChecked: false,
				placementStatus: BetPlacementStatus.EDIT,
				type: bet.type,
				stake: bet.stake,
				combination: bet.combination,
				betCount: calculateBetCount(bet.items.length, bet.combination),
				bonuses: [],
				items: bet.items.map(item => ({ ref: item.ref, selectionId: item.selectionId, finalOdds: 0 })),
				errors: [],
				warnings: [],
				acceptAllOddChanges: false
			}) as BetslipBet
	);

export const placeBetsResponseBetStatusToBetPlacementStatus = (
	status: PlaceBetsResponseBetStatus
): BetPlacementStatus => {
	switch (status) {
		case PlaceBetsResponseBetStatus.PLACED:
			return BetPlacementStatus.PLACED;
		case PlaceBetsResponseBetStatus.ERROR:
			return BetPlacementStatus.DECLINED;
		case PlaceBetsResponseBetStatus.DELAYED:
		default:
			return BetPlacementStatus.PLACING;
	}
};

export const createItemFromSelection = (selection: SelectionDto, tag?: string): Omit<BetslipDataItem, 'isLoading'> => ({
	ref: uniqueId(Date.now().toString()),
	selection,
	eventId: selection.eventId,
	marketId: selection.marketId,
	selectionId: selection.id,
	odds: selection.trueOdds,
	acceptedOdds: selection.trueOdds,
	hasReplacedData: false,
	tag: tag || undefined
});
export const bettingApiErrorToBetslipBetError = (
	error: CheckBetsResponseError | PlaceBetsResponseError
): BetslipBetError => ({
	betRef: error.betRef,
	type: BetslipBetErrorType[error.type],
	selectionIds: error.selectionIds,
	minStake: error.minStake,
	maxStake: error.maxStake
});

export const bettingApiWarningToBetslipBetWarning = (warning: CheckBetsResponseWarning): BetslipBetWarning => ({
	betRef: warning.betRef,
	type: BetslipBetWarningType[warning.type],
	selectionIds: warning.selectionIds
});

export const calculateAcceptedOdds = (trueOdds: number, acceptedOdds: number, acceptMode: AcceptMode) => {
	switch (acceptMode) {
		case AcceptMode.ACCEPT_ALL_CHANGES:
			return trueOdds;
		case AcceptMode.ACCEPT_HIGHER:
			return trueOdds >= acceptedOdds ? trueOdds : acceptedOdds;
		default:
			return acceptedOdds;
	}
};

export const calculateBetWithdrawStake = (bet: BetslipBet) => {
	// For system bet we should count for betCount to get a correct stake.
	if (bet.type === BetType.SYSTEM) {
		return bet.stake * (bet.betCount || 1);
	}

	return bet.stake;
};

export const betslipTabToBetTypeMap = {
	[BetslipTab.SINGLES]: BetType.SINGLE,
	[BetslipTab.COMBO]: BetType.COMBO,
	[BetslipTab.SYSTEM]: BetType.SYSTEM
};
